import { useAppSelector } from '../../../hooks/redux';
import { FinalReport } from '../../../types/AIClassification.types';
import { ReportResponse } from '../../../types/Report.types';
import { Divider } from '../../ui/Divider/Divider';
import { DescriptionAndReasoning } from '../../ui/Questionaire/DescriptionAndReasoning';
import { LegalBasis } from './LegalBasis/LegalBasis';
import { ProviderDeclaration } from './ProviderDeclaration/ProviderDeclaration';
import {
  buildDeclarationData,
  buildFooterData,
  buildHeaderData,
  buildLegalBasisData,
  mapClassificationToDisplayText
} from './Report.utils';
import { ReportFooter } from './ReportFooter/ReportFooter';
import { ReportHeader } from './ReportHeader/ReportHeader';

type QuestionnaireReportProps = {
  finalReport: FinalReport;
  classification?: string | null;
  legalBasis?: string | null;
  isEditable?: boolean;
  isDisabled?: boolean;
  onDescriptionUpdate?: (newDescription: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
};

const mapFinalReport = (
  finalReport: FinalReport,
  classification?: string | null,
  legalBasis?: string | null
): ReportResponse => ({
  classificationObligationsReport: {
    entityType: finalReport.entity_type,
    aiSystemName: finalReport.ai_system_name,
    version: finalReport.version,
    organizationName: finalReport.organization_name,
    complianceContactPerson: finalReport.compliance_contact_person,
    classification: finalReport.classification,
    generated: finalReport.generated,
    reportId: finalReport.report_id
  },
  aiSystemInformation: {
    description: finalReport.description
  },
  legalBasis: {
    // Only prepend the classification/legal-basis banner when the passed-in
    // classification agrees with the report's own classification, and derive the
    // banner name from the report itself. Guards against a stale/mismatched
    // classification job showing e.g. a high-risk banner on a minimal-risk report.
    obligations:
      classification &&
      legalBasis &&
      mapClassificationToDisplayText(classification) ===
        mapClassificationToDisplayText(finalReport.classification)
        ? [
            {
              name: mapClassificationToDisplayText(finalReport.classification),
              label: legalBasis,
              labelHtml: legalBasis
            },
            ...finalReport.obligations
          ]
        : finalReport.obligations
  },
  declaration: {
    text: finalReport.declaration_text,
    signedBy: finalReport.signedBy
  },
  footerNote: {
    text: finalReport.footer_text,
    generatedBy: finalReport.generated_by
  }
});

export function QuestionnaireReport({
  finalReport,
  classification,
  legalBasis,
  isEditable = true,
  isDisabled = false,
  onDescriptionUpdate,
  onEditStart,
  onEditEnd
}: QuestionnaireReportProps) {
  const { isEditClassificationPathEnabled } = useAppSelector(
    (state) => state.aiPowered
  );
  const data = mapFinalReport(finalReport, classification, legalBasis);

  const headerData = buildHeaderData(data);
  const legalBasisData = buildLegalBasisData(data);
  const declarationData = buildDeclarationData(data);
  const footerData = buildFooterData(data);

  return (
    <div
      className={`flex flex-col gap-5 ${isEditClassificationPathEnabled ? 'opacity-[0.38] pointer-events-none' : ''}`}
    >
      <ReportHeader componentInfo={headerData} />

      <Divider classes="my-3.5" />

      {data.aiSystemInformation.description.trim() !== '' && (
        <DescriptionAndReasoning
          title="AI System Information"
          content={data.aiSystemInformation.description}
          isDescription={true}
          isEditable={isEditable}
          isDisabled={isDisabled}
          onUpdate={onDescriptionUpdate}
          onEditStart={onEditStart}
          onEditEnd={onEditEnd}
          alwaysVisible={true}
        />
      )}

      <LegalBasis componentInfo={legalBasisData} />

      <Divider classes="my-3.5" />

      <ProviderDeclaration componentInfo={declarationData} />

      <Divider classes="my-3.5" />

      <ReportFooter componentInfo={footerData} />
    </div>
  );
}
